import React, { ComponentProps, useMemo } from 'react';
import { Checkbox, Tree } from '@myua/kit';
import { useSelector, useDispatch } from 'react-redux';
import { getProjects, getSelectedIds, getLoading, getExpanded } from '../selectors';
import * as projectActions from '../actions';
import '../styles.scss';
import { getAssetLabLogger } from '../../app/logs';
import { ArrowDown } from '@myua/icons/dist/linear14';
import { getPage } from '../../router/selectors';
import { useHistory } from 'react-router-dom';
import { Page, routes } from '../../router/model';
import { TreeSkeleton } from '../../skeleton/components/Skeleton';

const GROUP_KEY = '';

export const Project = () => {
  const dispatch = useDispatch();
  const projects = useSelector(getProjects);
  const selectedIds = useSelector(getSelectedIds);
  const loading = useSelector(getLoading);
  const expanded = useSelector(getExpanded);
  const expandedKeys = useMemo(() => (expanded ? [GROUP_KEY] : []), [expanded]);
  const assetLabLogger = useSelector(getAssetLabLogger);
  const page = useSelector(getPage);
  const history = useHistory();

  const treeData: ComponentProps<typeof Tree>['treeData'] = useMemo(() => {
    const onChange: ComponentProps<typeof Checkbox>['onChange'] = (e) => {
      // Вместо onChange на чебоксе можно было бы использовать onSelect на дереве.
      // Но в таком случае почему-то onSelect возникает два раза (с разными значениями).
      // Поэтому используем функционал чекбокса. А дерево нужно только для разворачивания группы и для стилей.

      if (page !== Page.viewer) {
        history.push(routes.viewer);
      }

      if (!(e.nativeEvent.target instanceof HTMLInputElement)) {
        return;
      }
      const id = Number(e.nativeEvent.target.dataset.id);
      if (Number.isNaN(id)) {
        return;
      }

      const ids = selectedIds.filter((item) => item !== id);
      if (e.target.checked) {
        ids.push(id);
      }

      dispatch(
        projectActions.projectIdsSelected({ ids, saveLastState: true, preserveSearch: true }),
      );
      assetLabLogger({ action: 'Filter', sub_action: 'Change project' });
    };

    const checked: Partial<Record<number, boolean>> = {};
    selectedIds.forEach((id) => {
      checked[id] = true;
    });

    return [
      {
        key: '',
        title: 'Project name',
        selectable: false,
        className: 'al-app-sider-tree__not-selectable',
        children: projects.map(({ id, name }) => ({
          key: id,
          className: 'al-projects__project',
          title: (
            <Checkbox
              checked={checked[id]}
              data-id={id}
              onChange={onChange}
              disabled={projects.length === 1}
            >
              {name}
            </Checkbox>
          ),
        })),
      },
    ];
  }, [projects, selectedIds, dispatch, assetLabLogger, page, history]);

  // Скелетон только когда показывать нечего — фоновое обновление проходит незаметно.
  if (loading && projects.length === 0) {
    return (
      <div aria-busy>
        <TreeSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div className="al-fade-in">
      <Tree
        className="al-app-sider-tree al-projects"
        switcherIcon={<ArrowDown />}
        treeData={treeData}
        multiple
        selectedKeys={selectedIds}
        expandedKeys={expandedKeys}
        onExpand={(keys) => {
          dispatch(
            projectActions.expandedChanged({
              expanded: keys.length === 1 && keys[0] === GROUP_KEY,
            }),
          );
        }}
      />
    </div>
  );
};
